/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Menu,
  ListSubheader,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import type { i18n, TFunction } from 'i18next';
import React from 'react';
import type { FieldError, FieldErrors } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  getDeepProperty,
  ReactHookFormHiddenInput,
  ReactHookFormSelect,
  useDraftFormHelpers,
} from '@opengeoweb/form-fields';
import {
  AlertBanner,
  CustomIconButton,
  CustomTooltip,
  dateUtils,
} from '@opengeoweb/shared';
import type { MapPreset } from '@opengeoweb/store';
import {
  Options,
  Delete,
  DragHandle,
  Remove,
  Expire,
  Polygons,
  Cube,
  DrawPolygon,
  Info,
} from '@opengeoweb/theme';
import { CollapseToggleButton } from './CollapseToggleButton';
import { getOverlappingWarnings } from './utils';
import type { TimelineEditing } from './WarningEventTimeline';
import { useTimelineRange, WarningEventTimeline } from './WarningEventTimeline';
import type { Area, WarningEventBody } from '../../../store/warningsForm/types';
import { PublicWarningStatus } from '../../../store/warningsForm/types';
import { useWarningsTranslation } from '../../../utils/i18n';
import { warningLevelFeatureColors } from '../../PublicWarningsForm';
import type { WarningLevel } from '../../PublicWarningsForm/LevelField';
import { WarningsMapView } from '../../WarningsMapView/WarningsMapView';

export const probablityOptions = [
  { name: '10 - 30', value: 10 },
  { name: '30 - 60', value: 30 },
  { name: '60 - 90', value: 60 },
  { name: '100', value: 100 },
];

const getWarningTitle = (
  status: PublicWarningStatus,
  areas: Area[],
  index: number,
  t: TFunction<'warn', i18n>,
): string => {
  const areaName =
    areas?.[0]?.objectName ||
    `${t('public-warning-event-type-warning')} ${index + 1}`;
  if (status === PublicWarningStatus.EXPIRED) {
    return `${areaName} - ${t('warning-event-warning-block-status-title-expired')}`;
  }
  if (status === PublicWarningStatus.WITHDRAWN) {
    return `${areaName} - ${t('warning-event-warning-block-status-title-withdrawn')}`;
  }
  return areaName;
};

const fieldsToValidate = ['validFrom', 'validUntil'];
const MAX_END_TIME_DAYS = 8; // max end time in days
const MIN_DURATION = 3600; // min duration of warning in seconds

const getValidationErrors = (
  fields: string[],
  errors: FieldErrors,
): { hasErrors: boolean; errors: FieldError[] } => {
  const fieldErrors = fields
    .map(
      (field) => getDeepProperty(field.split('.'), errors) as FieldError | null,
    )
    .filter((fieldError): fieldError is FieldError => !!fieldError);

  return { hasErrors: fieldErrors.length > 0, errors: fieldErrors };
};

export const WarningEventItem: React.FC<{
  defaultMapPreset?: MapPreset;
  index: number;
  isReadOnly: boolean;
  isSnapshot?: boolean;
  isCompact: boolean;
  isDeleteButtonDisabled: boolean;
  onExpireWithdraw: (status: PublicWarningStatus, index: number) => void;
  onToggleCompact: () => void;
  onDeleteWarning: (index: number) => void;
}> = ({
  defaultMapPreset,
  index,
  isReadOnly,
  isSnapshot = false,
  isCompact,
  isDeleteButtonDisabled = false,
  onExpireWithdraw,
  onToggleCompact,
  onDeleteWarning,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );

  const { t } = useWarningsTranslation();
  const { setValue, trigger, formState, getValues } = useFormContext();
  const { errors: fieldErrors } = formState;
  const timelineRange = useTimelineRange();
  const validFrom = useWatch({ name: `warnings.${index}.validFrom` });
  const validUntil = useWatch({ name: `warnings.${index}.validUntil` });
  const level = useWatch({ name: `warnings.${index}.level` });
  const warningStatus = useWatch({ name: `warnings.${index}.status` });
  const eventStatus = useWatch({ name: 'status' });
  const status = warningStatus || eventStatus;
  const linkedWarningId = useWatch({ name: `warnings.${index}.linked_to_id` });
  const warningAreas = useWatch({ name: `warnings.${index}.areas` });
  const { hasErrors: hasValidationErrors, errors: validationErrors } =
    getValidationErrors(
      fieldsToValidate.map((field) => `warnings.${index}.${field}`),
      fieldErrors,
    );
  const { hasErrors: hasProbabilityError, errors: probabilityError } =
    getValidationErrors([`warnings.${index}.probability`], fieldErrors);

  const editing: TimelineEditing = React.useMemo(
    () => ({
      onTimesChange: (newValidFrom: string, newValidUntil: string): void => {
        setValue(`warnings.${index}.validFrom`, newValidFrom, {
          shouldDirty: true,
        });
        setValue(`warnings.${index}.validUntil`, newValidUntil, {
          shouldDirty: true,
        });
        void trigger(`warnings.${index}.validUntil`);
        const warnings = getValues('warnings');
        warnings.forEach((warning: WarningEventBody, otherIndex: number) => {
          if (otherIndex !== index) {
            void trigger(`warnings.${otherIndex}.validUntil`);
          }
        });
      },
      onLevelChange: (newLevel: WarningLevel): void => {
        setValue(`warnings.${index}.level`, newLevel, { shouldDirty: true });

        const geoJSON = warningLevelFeatureColors(
          newLevel,
          warningAreas?.[0]?.geoJSON,
        );
        setValue(`warnings.${index}.areas.0.geoJSON`, geoJSON, {
          shouldDirty: true,
        });
        void trigger(`warnings.${index}.validUntil`);
      },
    }),
    [index, setValue, warningAreas, getValues, trigger],
  );
  // Until we implement the full validation, set every field to be required when publishing
  const { isRequired, isDraft } = useDraftFormHelpers();

  const collapseToggleButton = (
    <CollapseToggleButton
      collapseLabel={t('public-warning-event-collapse')}
      expandLabel={t('public-warning-event-expand')}
      isCollapsed={isCompact}
      onToggle={onToggleCompact}
    />
  );

  // An inactive warning can be withdrawn, an active warning can be expired
  const isActive = dateUtils.isBetween(
    dateUtils.getNowUtc(),
    dateUtils.createDate(validFrom)!,
    dateUtils.createDate(validUntil)!,
    '[]',
  );

  const onExpire = (): void => {
    onExpireWithdraw(PublicWarningStatus.EXPIRED, index);
  };

  const onWithdraw = (): void => {
    onExpireWithdraw(PublicWarningStatus.WITHDRAWN, index);
  };

  const readOnlyWarning =
    isReadOnly ||
    warningStatus === PublicWarningStatus.EXPIRED ||
    warningStatus === PublicWarningStatus.WITHDRAWN ||
    eventStatus === PublicWarningStatus.EXPIRED ||
    eventStatus === PublicWarningStatus.WITHDRAWN;

  const warningTitle = getWarningTitle(status, warningAreas, index, t);

  const mapTitle = warningAreas?.[0]?.objectName || undefined;
  return (
    <>
      {isCompact ? (
        <Box
          sx={{
            backgroundColor: 'geowebColors.background.surface',
            borderRadius: 1,
            padding: 1,
            marginBottom: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: 56,
          }}
        >
          <CustomIconButton
            className="handle"
            disabled={readOnlyWarning}
            sx={{ cursor: 'grab', flexShrink: 0 }}
            variant="flat"
          >
            <DragHandle />
          </CustomIconButton>
          <CustomTooltip title={warningTitle}>
            <Typography
              sx={{
                fontSize: 14,
                flexShrink: 0,
                width: `${hasValidationErrors || hasProbabilityError ? '93px' : '125px'}`,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              {warningTitle}
            </Typography>
          </CustomTooltip>
          {!!(hasValidationErrors || hasProbabilityError) && (
            <CustomTooltip
              title={`${t('warning-event-validation-warning-incomplete')}: ${validationErrors.map(
                (validationError) => `${validationError.message}`,
              )} ${probabilityError.map(
                (validationError) =>
                  `${t('warning-event-validation-propbability-collapsed')}`,
              )}`}
            >
              <span>
                <Info
                  sx={{
                    marginTop: 0.5,
                    color: `geowebColors.functional.error`,
                  }}
                />
              </span>
            </CustomTooltip>
          )}
          <Box
            sx={{
              width: 100,
              height: 56,
            }}
          >
            <WarningsMapView
              defaultMapPreset={defaultMapPreset}
              geojsonFeature={warningAreas?.[0]?.geoJSON || undefined}
              isSnapshot={isSnapshot}
              showMapMousePosition={false}
              showZoomControls={false}
              size={{ height: '56px', width: '100%' }}
            />
          </Box>
          <Box sx={{ flex: 1, height: 56, minWidth: 0 }}>
            <WarningEventTimeline
              end={timelineRange.end}
              isCompact
              level={level ?? ''}
              start={timelineRange.start}
              status={status}
              validFrom={validFrom ?? ''}
              validUntil={validUntil ?? ''}
            />
          </Box>
          {collapseToggleButton}
          <ReactHookFormHiddenInput
            name={`warnings.${index}.probability`}
            rules={{
              validate: {
                isRequired,
              },
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: 'geowebColors.background.surface',
            borderRadius: 1,
            padding: 1,
            marginBottom: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 150px 1fr',
              alignItems: 'center',
              gap: 1,
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
              }}
            >
              <CustomIconButton
                className="handle"
                disabled={readOnlyWarning}
                sx={{ cursor: 'grab', flexShrink: 0 }}
                variant="flat"
              >
                <DragHandle />
              </CustomIconButton>
              <Box sx={{ flex: 1, minWidth: 0, maxWidth: 260 }}>
                <Typography sx={{ fontSize: 14 }}>{warningTitle}</Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                '& .MuiFormControl-root': { width: '100%' },
              }}
            >
              <ReactHookFormSelect
                disabled={readOnlyWarning}
                label={t('warning-probability')}
                name={`warnings.${index}.probability`}
                rules={{
                  validate: {
                    isRequired,
                  },
                }}
              >
                {probablityOptions.map((probability) => (
                  <MenuItem key={probability.value} value={probability.value}>
                    {probability.name} %
                  </MenuItem>
                ))}
              </ReactHookFormSelect>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                justifySelf: 'end',
              }}
            >
              <CustomIconButton
                disabled={readOnlyWarning}
                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                variant="tool"
              >
                <Options />
              </CustomIconButton>
              {collapseToggleButton}
              <Menu
                anchorEl={menuAnchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                onClose={() => setMenuAnchorEl(null)}
                open={Boolean(menuAnchorEl)}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <ListSubheader sx={{ fontSize: 12, lineHeight: '1.5' }}>
                  {t('area-manager-share-menu-options')}
                </ListSubheader>
                {
                  // Only show delete item for unsaved, draft & draft published warnings that are new (aka have not already been published)
                  (eventStatus === undefined ||
                    eventStatus === PublicWarningStatus.DRAFT ||
                    eventStatus === PublicWarningStatus.DRAFT_PUBLISHED) &&
                    linkedWarningId === undefined && (
                      <MenuItem
                        disabled={isDeleteButtonDisabled}
                        onClick={() => {
                          setMenuAnchorEl(null);
                          onDeleteWarning(index);
                        }}
                      >
                        <ListItemText>
                          {t('public-warning-event-delete')}
                        </ListItemText>
                        <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                          <Delete />
                        </ListItemIcon>
                      </MenuItem>
                    )
                }
                {
                  // Event should already have been published but not already withdrawn or expired
                  linkedWarningId !== undefined &&
                    eventStatus === PublicWarningStatus.DRAFT_PUBLISHED &&
                    warningStatus !== PublicWarningStatus.EXPIRED &&
                    warningStatus !== PublicWarningStatus.WITHDRAWN && (
                      <>
                        <MenuItem
                          disabled={!isActive}
                          onClick={() => {
                            setMenuAnchorEl(null);
                            onExpire();
                          }}
                        >
                          <ListItemText>
                            {t('warning-list-button-expire')}
                          </ListItemText>
                          <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                            <Expire />
                          </ListItemIcon>
                        </MenuItem>
                        <MenuItem
                          disabled={isActive}
                          onClick={() => {
                            setMenuAnchorEl(null);
                            onWithdraw();
                          }}
                        >
                          <ListItemText>
                            {t('warning-list-button-withdraw')}
                          </ListItemText>
                          <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                            <Remove />
                          </ListItemIcon>
                        </MenuItem>
                      </>
                    )
                }
              </Menu>
            </Box>
          </Box>
          {!!hasValidationErrors && (
            <Box sx={{ marginBottom: 2 }}>
              <AlertBanner
                info={
                  <>
                    {validationErrors.map((validationError, errorIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={errorIndex}>{validationError.message}</div>
                    ))}
                  </>
                }
                severity="error"
                title={t('warning-event-validation-warning-incomplete')}
              />
            </Box>
          )}
          <Grid container spacing={1}>
            <Grid size={4}>
              <Box
                sx={{
                  height: 200,

                  position: 'relative',
                }}
              >
                <WarningsMapView
                  defaultMapPreset={defaultMapPreset}
                  geojsonFeature={warningAreas?.[0]?.geoJSON || undefined}
                  isSnapshot={isSnapshot}
                  showMapMousePosition={false}
                  size={{ height: '100%', width: '100%' }}
                />
                {!readOnlyWarning && (
                  <>
                    <CustomIconButton
                      isSelected={false}
                      onClick={() => {}}
                      sx={{
                        position: 'absolute',
                        left: 8,
                        bottom: 40,
                        zIndex: 1,
                      }}
                      tooltipTitle={t('warning-button-draw-polygon')}
                      variant="tool"
                    >
                      <Polygons />
                    </CustomIconButton>
                    <CustomIconButton
                      isSelected={false}
                      onClick={() => {}}
                      sx={{
                        position: 'absolute',
                        left: 8,
                        bottom: 8,
                        zIndex: 1,
                      }}
                      tooltipTitle={t('warning-button-add-drawing')}
                      variant="tool"
                    >
                      <Cube />
                    </CustomIconButton>
                  </>
                )}
              </Box>
              {!!mapTitle && (
                <Box
                  sx={{
                    border: '1px solid',
                    mt: 1,
                    borderColor: 'geowebColors.cards.cardContainerBorder',
                    height: '32px',
                  }}
                >
                  <Grid container sx={{ height: '100%' }}>
                    <Grid
                      size={9}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                        pl: '8px',
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                        {mapTitle}
                      </Typography>
                    </Grid>
                    <Grid
                      size={3}
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        height: '100%',
                      }}
                    >
                      <CustomIconButton
                        disabled={readOnlyWarning}
                        isSelected={false}
                        onClick={() => {}}
                        sx={{ mr: '8px' }}
                        tooltipTitle={t(
                          'warning-event-map-edit-drawing-tooltip',
                        )}
                        variant="flat"
                      >
                        <DrawPolygon />
                      </CustomIconButton>
                      <CustomIconButton
                        disabled={readOnlyWarning}
                        isSelected={false}
                        onClick={() => {}}
                        sx={{ mr: '8px' }}
                        tooltipTitle={t(
                          'warning-event-map-remove-drawing-tooltip',
                        )}
                        variant="flat"
                      >
                        <Delete />
                      </CustomIconButton>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Grid>
            <Grid size={8}>
              <Box sx={{ height: '100%' }}>
                <WarningEventTimeline
                  editing={readOnlyWarning ? undefined : editing}
                  end={timelineRange.end}
                  level={level ?? ''}
                  start={timelineRange.start}
                  status={status}
                  validFrom={validFrom ?? ''}
                  validUntil={validUntil ?? ''}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
      <ReactHookFormHiddenInput
        name={`warnings.${index}.validFrom`}
        rules={{
          validate: {
            minStartTime: (value): boolean | string => {
              const now = dateUtils.getNowUtc();
              return (status === undefined ||
                status === PublicWarningStatus.DRAFT) &&
                dateUtils.unixFromISOString(value) < dateUtils.unix(now)
                ? t('warning-event-validation-min-start-time-draft')
                : true;
            },
          },
        }}
      />
      <ReactHookFormHiddenInput
        name={`warnings.${index}.validUntil`}
        rules={{
          validate: {
            maxEndTime: (value): boolean | string => {
              const dayZero = dateUtils.startOfDayUTC(dateUtils.getNowUtc());
              const maxEnd = dateUtils.add(dayZero, {
                days: MAX_END_TIME_DAYS,
              });
              return dateUtils.unixFromISOString(value) > dateUtils.unix(maxEnd)
                ? t('warning-event-validation-max-end-time', {
                    MAX_DAYS: MAX_END_TIME_DAYS,
                  })
                : true;
            },
            minDuration: (value): boolean | string => {
              if (isDraft()) {
                return true;
              }
              const validFrom = getValues(`warnings.${index}.validFrom`);
              const timeDiff =
                dateUtils.unixFromISOString(value) -
                dateUtils.unixFromISOString(validFrom);
              return timeDiff < MIN_DURATION
                ? t('warning-event-validation-min-duration')
                : true;
            },
            noOverlap: (_value, formValues): boolean | string => {
              if (isDraft()) {
                return true;
              }
              const warnings =
                (formValues as { warnings?: WarningEventBody[] }).warnings ??
                [];
              return getOverlappingWarnings(warnings, index).length > 0
                ? t('warning-event-validation-overlap')
                : true;
            },
          },
        }}
      />
    </>
  );
};

export default WarningEventItem;
